<template>
  <div class="business-list-column">
    <div class="business-container">
      <div class="busines-menu">
        <b-dropdown
          no-caret
          right
          no-flip
          toggle-class="menu-toggle"
          menu-class="menu-items"
          class="business-dropdown">
          <template slot="button-content">
            <div
              v-b-tooltip="getTooltipConfig(merchantName)"
              class="business-item">
              <div>
                <span
                  :style="merchantLogo ? `background-image: url(${merchantLogo})` : ''"
                  class="logo"/>
                <span
                  :class="{green: merchantSettings.available, red: !merchantSettings.available}"
                  class="indicator"/>
              </div>
            </div>
          </template>
          <div
            class="dropdown-action"
            @click.stop>
            <div class="action-title">
              <span
                :class="{green: merchantSettings.available, red: !merchantSettings.available}"
                class="indicator"/>
              {{ merchantSettings.available ? 'Available' : 'Away' }}
            </div>
            <span class="description">
              {{ merchantSettings.available ?
                'Let the contacts know your business is open for conversation.' :
                'Let the contacts know your business is closed for conversation.' }}
            </span>
            <SwitchCheckbox
              v-model="merchantSettings.available"
              class="dropdown-check"
              size="mini"
              @input="changeMerchantAvailability"/>
          </div>
          <b-dropdown-item @click="openShareNumberModal">
            Share WhatsApp Number
          </b-dropdown-item>
          <b-dropdown-item :to="{ name: 'BusinessSettings', params: { back_to: $route.path } }">
            Business Settings
          </b-dropdown-item>
        </b-dropdown>
        <nav>
          <router-link
            v-b-tooltip="getTooltipConfig('Chat')"
            :to="{ name: 'Chat' }"
            active-class="active"
            class="menu-item mt-3">
            <span
              v-if="userHasUnreadMessages && userNotInChat"
              class="has-unread"/>
            <span class="menu-icon chat"/>
          </router-link>
          <router-link
            v-b-tooltip="getTooltipConfig('Notifications')"
            :to="{ name: 'SentNotifications' }"
            :class="{active: routeIs('Notifications')}"
            class="menu-item">
            <span class="menu-icon notifications"/>
          </router-link>
          <router-link
            v-b-tooltip="getTooltipConfig('Automation')"
            :to="{ name: 'Automation' }"
            :class="{active: routeIs('Automation')}"
            class="menu-item">
            <span class="menu-icon automation"/>
          </router-link>
          <router-link
            v-b-tooltip="getTooltipConfig('Contacts')"
            :to="{ name: 'Contacts' }"
            active-class="active"
            class="menu-item">
            <span class="menu-icon contacts"/>
          </router-link>
          <router-link
            v-b-tooltip="getTooltipConfig('Analytics')"
            :to="{ name: 'Analytics' }"
            active-class="active"
            class="menu-item">
            <span class="menu-icon analytics"/>
          </router-link>
        </nav>
      </div>
    </div>
    <UserDropdown/>
    <ShareNumberModal
      ref="shareNumberModal"
      :phone="phoneNumber | digits"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import NotificationSoundMixin from '@/mixins/NotificationSound';
import MerchantAvailabilityMixin from '@/mixins/MerchantAvailability';
import ShareNumberModal from './modals/ShareNumberModal';
import UserDropdown from './UserDropdown';
import SwitchCheckbox from './SwitchCheckbox';

export default {
  name: 'BusinessMenuColumn',
  components: {
    UserDropdown,
    SwitchCheckbox,
    ShareNumberModal,
  },
  mixins: [
    NotificationSoundMixin,
    MerchantAvailabilityMixin,
  ],
  props: {
    businessView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      userId: localStorage.getItem('userId'),
      titleNotification: '',
      pageTitle: document.title,
      documentVisible: document.visible,
      notificationInterval: 0,
    };
  },
  computed: {
    ...mapGetters({
      merchantActivity: 'pusher/merchantActivity',
      merchant: 'merchant/merchant',
      merchantName: 'merchant/merchantName',
      merchantLogo: 'merchant/merchantLogo',
      unreadConversationsCount: 'chat/unreadConversationsCount',
      phoneNumber: 'merchant/whatsappPhoneNumber',
    }),
    userNotInChat() {
      return this.$route.name !== 'Chat';
    },
    userHasUnreadMessages() {
      if (this.$lodash.isEmpty(this.merchant.unread_conversations)) {
        return false;
      }

      return !!this.merchant.unread_conversations.all_unread || !!this.merchant.unread_conversations.user_unread[this.userId];
    },
  },
  watch: {
    titleNotification(notification) {
      if (notification) {
        this.addNotificationTitle();
      } else {
        this.removeNotificationTitle();
      }
    },
    merchantActivity(activity) {
      if ((activity.type === 'assignment') &&
          ((activity.meta_data.from_user_id === this.userId) || (activity.meta_data.to_user_id !== this.userId))
      ) {
        return;
      }

      if (activity.merchant_id === this.merchant.id) {
        this.merchant.unread_conversations = activity.merchant_unread_conversations;
      }

      if (document.hidden) {
        this.titleNotification = (activity.type === 'assignment') ? '*New Conversation Assigned*' : '*New Message*';
      }

      if (this.$route.name !== 'Chat') {
        this.notifySound();
      }
    },
    unreadConversationsCount(unreadData) {
      if (unreadData && unreadData.merchant_id && (unreadData.merchant_id === this.merchant.id)) {
        this.merchant.unread_conversations = unreadData.unread_conversations;
      }
    },
  },
  mounted() {
    window.onfocus = () => {
      this.titleNotification = '';
    };
  },
  methods: {
    ...mapActions({
      fetchMerchants: 'merchant/fetchMerchants',
    }),
    routeIs(routeName) {
      // need to check this way as active-class property doesnt work for multiple children
      // in case you redirect parent route to one of the child routes
      return this.$route.matched[1].name === routeName;
    },
    addNotificationTitle() {
      this.changeFavIcon('/favicon-dot.ico');
      this.notificationInterval = setInterval(() => {
        document.title = (document.title === this.pageTitle) ? this.titleNotification : this.pageTitle;
      }, 2000);
    },
    removeNotificationTitle() {
      this.changeFavIcon('/favicon.ico');
      clearInterval(this.notificationInterval);
      document.title = this.pageTitle;
    },
    changeFavIcon(src) {
      const favicon = document.getElementById('favicon');
      favicon.setAttribute('href', src);
    },
    openShareNumberModal() {
      this.$refs.shareNumberModal.show();
    },
    getTooltipConfig(text) {
      return {
        title: text,
        placement: 'right',
        offset: '0, 5px',
        animation: false,
      };
    },
  },
};
</script>
